import React, { useState, useEffect } from "react";

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll("[data-section]");
      let current = "";
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 100) {
          current = section.getAttribute("data-section");
        }
      });
      setActiveSection(current);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const sections = [
    { id: "introduction", title: "Introduction" },
    { id: "information-collection", title: "Information Collection" },
    { id: "permissions", title: "Permissions" },
    { id: "data-security", title: "Data Security" },
    { id: "third-party", title: "Third-Party Services" },
    { id: "children", title: "Children's Privacy" },
    { id: "changes", title: "Policy Changes" },
    { id: "contact", title: "Contact Us" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-md rounded-2xl mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              SapEagle ERP Pvt. Ltd. - Your privacy is our priority
            </p>
            <p className="text-sm text-blue-200 mt-2">Last updated: May 2026</p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="lg:grid lg:grid-cols-12 lg:gap-12">
          {/* Table of Contents - Desktop */}
          <nav className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24">
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  Contents
                </h3>
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <a
                        href={`#${section.id}`}
                        className={`block text-sm transition-all ${
                          activeSection === section.id
                            ? "text-blue-600 font-medium"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        {section.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </nav>

          {/* Main Content */}
          <div className="lg:col-span-9">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <div className="p-8 sm:p-12">
                {/* Introduction */}
                <section data-section="introduction" id="introduction" className="scroll-mt-24">
                  <div className="flex items-start mb-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center mr-4">
                      <span className="text-2xl">📋</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Introduction</h2>
                      <p className="text-gray-600 mt-1">Understanding our commitment to your privacy</p>
                    </div>
                  </div>
                  <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed">
                    <p>
                      <strong className="text-gray-800">SapEagle ERP Pvt. Ltd.</strong> built the SapEagle ERP app as a commercial application.
                      This service is provided by SapEagle ERP Pvt. Ltd. and is intended for use as is.
                    </p>
                    <p className="mt-4">
                      This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information
                      when You use the Service and tells You about Your privacy rights and how the law protects You.
                    </p>
                  </div>
                </section>

                <Section
                  id="information-collection"
                  icon="📊"
                  title="Information Collection and Use"
                  description="The app may collect certain information including but not limited to:"
                  items={[
                    "Name",
                    "Email address",
                    "Mobile number",
                    "Business/company information",
                    "Device information",
                    "App usage data",
                  ]}
                  footer="This information is collected to improve our services and provide a better user experience."
                />

                <Section
                  id="permissions"
                  icon="🔐"
                  title="Permissions"
                  description="The app requires the following permissions for functionality:"
                  items={["Internet access", "Storage access", "Camera access"]}
                  footer="These permissions are only used for app functionality and to provide you with the best possible experience."
                />

                <Section
                  id="data-security"
                  icon="🛡️"
                  title="Data Security"
                  description="We value your trust in providing your personal information. We strive to use commercially acceptable means of protecting it. However, no method of transmission over the internet or electronic storage is 100% secure."
                />

                <Section
                  id="third-party"
                  icon="🔗"
                  title="Third-Party Services"
                  description="The app may use third-party services such as:"
                  items={["Google Play Services", "Firebase Analytics"]}
                  footer="These services may collect information used to identify you. We recommend reviewing the privacy policies of these third-party services."
                />

                <Section
                  id="children"
                  icon="👶"
                  title="Children's Privacy"
                  description="This app is not intended for children under the age of 13. We do not knowingly collect personally identifiable information from children under 13. In the case we discover that a child under 13 has provided us with personal information, we immediately delete this from our servers."
                />

                <Section
                  id="changes"
                  icon="📝"
                  title="Changes to This Privacy Policy"
                  description="We may update our Privacy Policy from time to time. Users are advised to review this page periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page."
                />

                <section data-section="contact" id="contact" className="scroll-mt-24 mt-16">
                  <div className="flex items-start mb-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center mr-4">
                      <span className="text-2xl">📧</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
                      <p className="text-gray-600 mt-1">Have questions? We're here to help</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold text-gray-800">SapEagle ERP Pvt. Ltd.</h3>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <svg className="w-5 h-5 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <a href="mailto:support@sapeagleerp.com" className="text-blue-600 hover:text-blue-800 font-medium transition-colors">
                          support@sapeagleerp.com
                        </a>
                      </div>
                    </div>
                  </div>
                </section>

                {/* CTA Button */}
                <div className="mt-16 text-center">
                  <a
                    href="https://sites.google.com/view/sapeagle-privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    View Official Privacy Policy
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ id, icon, title, description, items, footer }) => {
  return (
    <section data-section={id} id={id} className="scroll-mt-24 mt-16">
      <div className="flex items-start mb-6">
        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center mr-4">
          <span className="text-2xl">{icon}</span>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          {description && <p className="text-gray-600 mt-1">{description}</p>}
        </div>
      </div>

      {items && (
        <div className="ml-16">
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {items.map((item, index) => (
              <li key={index} className="flex items-center text-gray-600">
                <span className="w-2 h-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full mr-3" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {description && !items && (
        <div className="ml-16 prose prose-lg max-w-none text-gray-600 leading-relaxed">
          <p>{description}</p>
        </div>
      )}

      {footer && (
        <p className="ml-16 mt-4 text-gray-600 leading-relaxed">{footer}</p>
      )}
    </section>
  );
};

export default PrivacyPolicy;